export const teamTranslations = {
  en: {
    backToHome: '← Back to Home',
    badge: 'Meet Our Team',
    title: 'DESTA TEAM',
    subtitle: 'SOFTWARE DEVELOPERS',
    description: 'Meet the passionate developers behind DESTA who bring your ideas to life with cutting-edge technology and innovative solutions.',
    cta: 'Ready to work with our amazing team? Let\'s discuss your project and see how we can help you achieve your goals.',
    contactButton: 'Get in Touch',
    
    // ========== SECTION 1: WHO WE ARE ==========
    aboutBadge: 'About',
    whoWeAre: 'WHO WE ARE',
    whoWeAreHeadline: 'Product-focused software development team building professional digital products with the latest AI and Tech innovations',
    whoWeAreSubheadline: 'We start with your product and business goals — and only then choose the right technologies. No stack limitations. No template solutions.',
    whoWeAreCta1: 'Start with a Product Review',
    whoWeAreCta2: 'Build a Proof of Concept',
    
    // ========== SECTION 2: WHAT MAKES US DIFFERENT ==========
    whatMakesUsDifferent: 'WHAT MAKES US DIFFERENT',
    whatMakesUsDifferentDesc: 'Before writing a single line of code, we study your idea, business model, users, and long-term goals. Only after that do we design the system and select the technology stack that fits your product, not our preferences.',
    whatMakesUsDifferentBullets: [
      'Product discovery & analysis',
      'System architecture first',
      'Technology decisions based on real needs'
    ],
    
    // ========== SECTION 3: OUR APPROACH ==========
    ourApproach: 'OUR APPROACH',
    ourApproachSubtitle: 'How we think and work',
    approachPillars: {
      productFirst: {
        title: 'Product-first mindset',
        desc: 'Every decision starts with the product and the problem it solves.'
      },
      aiAssisted: {
        title: 'AI-assisted development',
        desc: 'AI helps us move faster, while engineers focus on architecture, quality, and business logic.'
      },
      noLimitations: {
        title: 'Technology without limitations',
        desc: 'We work with any tools and platforms required to build the right solution.'
      }
    },
    
    // ========== SECTION 4: WHAT WE BUILD ==========
    whatWeBuild: 'WHAT WE BUILD',
    whatWeBuildItems: [
      'Mobile applications',
      'Web platforms',
      'MVPs & early-stage products',
      'Complex systems and integrations'
    ],
    whatWeBuildMicro: 'Built for real users, real businesses, and long-term scalability.',
    
    // ========== SECTION 5: TECHNOLOGY-AGNOSTIC ==========
    techAgnostic: 'TECHNOLOGY-AGNOSTIC BY DESIGN',
    techAgnosticDesc: 'We are not tied to any specific framework or platform. Our team selects technologies based on your product requirements, scalability needs, budget, and future growth.',
    techAgnosticHighlights: [
      'Any frontend, backend, or infrastructure',
      'Client-preferred tools and platforms',
      'Modern, proven, or legacy-friendly solutions'
    ],
    
    // ========== SECTION 6: WHO WE WORK WITH ==========
    whoWeWorkWith: 'WHO WE WORK WITH',
    whoWeWorkWithItems: [
      'Founders and product owners',
      'Startups at any stage',
      'Businesses with existing systems',
      'Teams that need a flexible, senior-level partner'
    ],
    
    // ========== SECTION 7: WHAT MAKES US DIFFERENT (SUMMARY) ==========
    differencesSummary: 'WHAT SETS US APART',
    differencesSummaryItems: [
      'Product thinking before code',
      'Technology-agnostic development',
      'AI as a development accelerator',
      'Clear architecture and scalable systems',
      'Transparent, result-first collaboration'
    ],
    
    // ========== SECTION 8: ZERO-RISK START ==========
    zeroRisk: 'ZERO-RISK START',
    zeroRiskDesc: 'You don\'t pay upfront. We deliver a working proof of concept first — then you decide how to move forward.',
    zeroRiskBullets: [
      'No prepayments',
      'No hidden fees',
      'Results before payment'
    ],
    
    // ========== SECTION 9: HOW WE WORK ==========
    howWeWork: 'HOW WE WORK',
    howWeWorkSteps: [
      'Product discovery & analysis',
      'Architecture & system design',
      'Technology selection',
      'Proof of concept',
      'Production development & launch'
    ],
    // Core Engineering Skills
    coreEngineering: 'CORE ENGINEERING SKILLS',
    ourExpertise: 'Our Skills',
    webDevelopment: {
      title: 'Web Development',
      desc: 'Modern, responsive, and performant web applications built with cutting-edge technologies:\n\n• Frontend: React, Next.js, Vue.js, Angular, TypeScript\n• Styling: Tailwind CSS, CSS3, SASS, Styled Components\n• State Management: Redux, MobX, Zustand, Context API\n• Build Tools: Webpack, Vite, Rollup\n• Testing: Jest, Cypress, Testing Library\n• Performance: Code splitting, lazy loading, SEO optimization\n\nWe create fast, scalable, and maintainable web applications that deliver exceptional user experiences.'
    },
    mobileDevelopment: {
      title: 'Mobile Development', 
      desc: 'Native and cross-platform mobile applications for iOS and Android:\n\n• Cross-Platform: React Native, Flutter, Expo\n• iOS Native: Swift, SwiftUI, UIKit, Core Data\n• Android Native: Kotlin, Jetpack Compose, Room\n• Mobile APIs: Push notifications, Camera, Geolocation\n• State Management: Redux, MobX, Provider\n• Testing: Detox, Appium, XCTest, Espresso\n\nBuild once, deploy everywhere - or go native for maximum performance. We do both!'
    },
    backendDatabases: {
      title: 'Backend & Database',
      desc: 'Robust server-side solutions and efficient data management:\n\n• Backend: Node.js, Python, Go, Java, .NET\n• Frameworks: Express, Django, Flask, Spring Boot\n• Databases: PostgreSQL, MySQL, MongoDB, Redis\n• Cloud: Supabase, Firebase, AWS, Google Cloud\n• APIs: RESTful, GraphQL, WebSockets, gRPC\n• Authentication: JWT, OAuth, Auth0, Passport\n• DevOps: Docker, Kubernetes, CI/CD pipelines\n\nScalable architecture designed to handle millions of users and transactions.'
    },
    apis: {
      title: 'APIs & Integrations',
      desc: 'Seamless integrations connecting your systems and automating workflows:\n\n• API Development: RESTful, GraphQL, WebSockets\n• Third-Party Integrations: Payment gateways, Social media, CRM systems\n• Popular Services: Stripe, PayPal, Twilio, SendGrid\n• Authentication: OAuth 2.0, JWT, API keys\n• Documentation: OpenAPI/Swagger, Postman\n• Security: Rate limiting, encryption, CORS\n• Webhooks: Real-time event handling\n\nConnect anything to everything - from payment processing to AI services.'
    },
    ecommerce: {
      title: 'E-commerce',
      desc: 'Complete e-commerce solutions from concept to launch:\n\n• Platforms: Custom solutions, Shopify, WooCommerce\n• Payment Integration: Stripe, PayPal, Square, Crypto\n• Inventory Management: Real-time tracking, warehousing\n• Shopping Cart: Secure checkout, order processing\n• User Management: Accounts, wishlists, order history\n• Admin Dashboard: Sales analytics, product management\n• Marketing: SEO, email campaigns, discount codes\n\nTurn your business idea into a profitable online store with 24/7 sales capability.'
    },
    ai: {
      title: 'AI & LLM Solutions',
      desc: 'Cutting-edge AI solutions powered by the latest language models:\n\n• LLM Integration: GPT-4, Claude, Gemini, Llama\n• AI Tools: LangChain, Hugging Face, OpenAI API\n• Use Cases: Chatbots, content generation, analysis\n• RAG Systems: Custom knowledge bases, document Q&A\n• Vector Databases: Pinecone, Weaviate, Chroma\n• Fine-tuning: Custom model training and optimization\n• Token Optimization: Cost reduction, prompt engineering\n• AI Agents: Autonomous task execution\n\nHarness the power of AI to automate, analyze, and innovate like never before.'
    },
    webScraping: {
      title: 'Web Scraping',
      desc: 'Professional data extraction and web scraping solutions:\n\n• Tools: Scrapy, BeautifulSoup, Playwright, Puppeteer\n• Techniques: Dynamic content, JavaScript rendering\n• Anti-Detection: Proxies, browser fingerprinting, CAPTCHA solving\n• Data Processing: Cleaning, transformation, storage\n• Automation: Scheduled scraping, monitoring\n• Providers: Bright Data, ScraperAPI, Oxylabs\n• Compliance: Respecting robots.txt, rate limiting\n\nExtract valuable data from any website for market research, price monitoring, or lead generation.'
    },
    aiAgents: {
      title: 'AI Agents Creation',
      desc: 'Intelligent automation agents that work 24/7 for your business:\n\n• Workflow Automation: n8n, Zapier, Make.com\n• AI Assistants: Custom chatbots, virtual assistants\n• Task Automation: Email processing, data entry\n• Testing & QA: Selenium, Playwright, automated testing\n• Monitoring: System health checks, alerts\n• Integration: Connect multiple services and APIs\n• Business Logic: Complex decision-making workflows\n\nCreate autonomous agents that handle repetitive tasks, freeing your team for strategic work.'
    },
    // Core Competencies Section
    coreCompetencies: {
      title: 'Our Competencies',
      mainTitle: 'Technical Excellence & Innovation',
      subtitle: 'Mastering cutting-edge technologies and industry best practices to deliver innovative solutions that drive your business forward.',
      programmingLanguages: 'PROGRAMMING LANGUAGES',
      softSkills: 'Essential Soft Skills',
      professionalMindset: 'Professional Mindset',
      professionalFoundation: 'OUR PROFESSIONAL FOUNDATION',
      skillsList: {
        problemSolving: 'Problem-Solving & Communication',
        teamwork: 'Teamwork & Collaboration',
        adaptability: 'Adaptability',
        timeManagement: 'Time Management',
        attentionToDetail: 'Attention to Detail'
      },
      mindsetList: {
        continuousLearning: 'Continuous Learning',
        userCentric: 'User-Centric Thinking',
        securityAwareness: 'Security Awareness',
        businessUnderstanding: 'Business Understanding',
        ownership: 'Ownership & Responsibility'
      }
    },
    // Pricing Section
    pricing: {
      title: 'Transparent Pricing',
      subtitle: 'Our hourly rates range from $20 to $70 depending on project complexity, required expertise, delivery speed, and project duration.',
      hourlyRates: 'Hourly Rates',
      perHour: 'per hour, depending on:',
      factors: {
        complexity: 'Project Complexity',
        expertise: 'Required Expertise',
        speed: 'Delivery Speed',
        duration: 'Project Duration'
      },
      supportMaintenance: 'Support & Maintenance',
      supportPlansCards: {
        basicSupport: 'Basic Support',
        standardSupport: 'Standard Support',
        premiumSupport: 'Premium Support'
      },
      supportPlansPrices: {
        basicSupport: 'from $200/month',
        standardSupport: 'from $500/month',
        premiumSupport: 'from $800/month'
      },
      freeSupport: '1 month of free support included for all projects developed by our team',
      supportPlans: {
        basicSupport: {
          title: 'Basic Support',
          desc: 'Essential maintenance and support:\n\n• Critical bug fixes\n• Security updates\n• Email support\n• Response time: 48 hours\n\nPerfect for stable applications that need minimal ongoing attention.'
        },
        standardSupport: {
          title: 'Standard Support',
          desc: 'Comprehensive support package:\n\n• All from Basic\n• New feature additions\n• Performance optimization\n• Priority support\n• Response time: 24 hours\n\nIdeal for growing applications that need regular updates.'
        },
        premiumSupport: {
          title: 'Premium Support',
          desc: 'Complete enterprise-level support:\n\n• All from Standard\n• Personal manager\n• Video consultations\n• Weekly reports\n• Response time: 4 hours\n\nPerfect for mission-critical applications requiring maximum attention.'
        }
      }
    },
    // Fixed Price Projects Section
    fixedPriceProjects: {
      title: 'FIXED PRICE PROJECTS',
      apiIntegration: {
        title: 'API Integration',
        price: 'from $300',
        desc: 'Seamless API integrations to connect your systems and automate workflows.',
        features: [
          'Third-party API integration',
          'Custom middleware development',
          'Data synchronization',
          'Authentication setup',
          'Error handling & monitoring',
          '2 months support included'
        ]
      },
      websiteCreation: {
        title: 'Website Creation',
        price: 'from $300',
        desc: 'Professional websites built with modern technologies and best practices.',
        features: [
          'Responsive design',
          'Modern UI/UX',
          'SEO optimization',
          'Content management',
          'Performance optimization',
          '3 months support included'
        ]
      }
    }
  },
  uk: {
    backToHome: '← Повернутися додому',
    badge: 'Наша Команда',
    title: 'DESTA TEAM',
    subtitle: 'РОЗРОБНИКИ ПРОГРАМНОГО ЗАБЕЗПЕЧЕННЯ',
    description: 'Зустрічайте пристрасних розробників DESTA, які втілюють ваші ідеї в життя за допомогою передових технологій та інноваційних рішень.',
    cta: 'Готові працювати з нашою чудовою командою? Давайте обговоримо ваш проект і подивимося, як ми можемо допомогти вам досягти ваших цілей.',
    contactButton: 'Зв\'язатися з нами',
    
    // ========== SECTION 1: WHO WE ARE ==========
    aboutBadge: 'Про нас',
    whoWeAre: 'ХТО МИ',
    whoWeAreHeadline: 'Продуктово-орієнтована команда розробників, що створює професійні цифрові продукти з використанням найновіших AI та Tech інновацій',
    whoWeAreSubheadline: 'Ми починаємо з вашого продукту та бізнес-цілей — і тільки потім обираємо правильні технології. Без обмежень стеку. Без шаблонних рішень.',
    whoWeAreCta1: 'Почати з Product Review',
    whoWeAreCta2: 'Створити Proof of Concept',
    
    // ========== SECTION 2: WHAT MAKES US DIFFERENT ==========
    whatMakesUsDifferent: 'ЩО РОБИТЬ НАС ОСОБЛИВИМИ',
    whatMakesUsDifferentDesc: 'Перш ніж написати хоча б один рядок коду, ми вивчаємо вашу ідею, бізнес-модель, користувачів та довгострокові цілі. Тільки після цього ми проектуємо систему та обираємо технологічний стек, який підходить вашому продукту, а не нашим уподобанням.',
    whatMakesUsDifferentBullets: [
      'Product discovery та аналіз',
      'Спочатку системна архітектура',
      'Технологічні рішення на основі реальних потреб'
    ],
    
    // ========== SECTION 3: OUR APPROACH ==========
    ourApproach: 'НАШ ПІДХІД',
    ourApproachSubtitle: 'Як ми думаємо та працюємо',
    approachPillars: {
      productFirst: {
        title: 'Product-first мислення',
        desc: 'Кожне рішення починається з продукту та проблеми, яку він вирішує.'
      },
      aiAssisted: {
        title: 'AI-асистована розробка',
        desc: 'AI допомагає нам рухатися швидше, тоді як інженери фокусуються на архітектурі, якості та бізнес-логіці.'
      },
      noLimitations: {
        title: 'Технології без обмежень',
        desc: 'Ми працюємо з будь-якими інструментами та платформами, необхідними для створення правильного рішення.'
      }
    },
    
    // ========== SECTION 4: WHAT WE BUILD ==========
    whatWeBuild: 'ЩО МИ СТВОРЮЄМО',
    whatWeBuildItems: [
      'Мобільні додатки',
      'Веб-платформи',
      'MVP та продукти на ранній стадії',
      'Складні системи та інтеграції'
    ],
    whatWeBuildMicro: 'Створено для реальних користувачів, реального бізнесу та довгострокової масштабованості.',
    
    // ========== SECTION 5: TECHNOLOGY-AGNOSTIC ==========
    techAgnostic: 'ТЕХНОЛОГІЧНО НЕЗАЛЕЖНІ ЗА ДИЗАЙНОМ',
    techAgnosticDesc: 'Ми не прив\'язані до жодного конкретного фреймворку чи платформи. Наша команда обирає технології на основі вимог вашого продукту, потреб масштабування, бюджету та майбутнього зростання.',
    techAgnosticHighlights: [
      'Будь-який frontend, backend або інфраструктура',
      'Інструменти та платформи за вибором клієнта',
      'Сучасні, перевірені або legacy-friendly рішення'
    ],
    
    // ========== SECTION 6: WHO WE WORK WITH ==========
    whoWeWorkWith: 'З КИМ МИ ПРАЦЮЄМО',
    whoWeWorkWithItems: [
      'Засновники та власники продуктів',
      'Стартапи на будь-якій стадії',
      'Бізнеси з існуючими системами',
      'Команди, яким потрібен гнучкий senior-level партнер'
    ],
    
    // ========== SECTION 7: WHAT MAKES US DIFFERENT (SUMMARY) ==========
    differencesSummary: 'ЩО НАС ВИРІЗНЯЄ',
    differencesSummaryItems: [
      'Продуктове мислення перед кодом',
      'Технологічно незалежна розробка',
      'AI як прискорювач розробки',
      'Чітка архітектура та масштабовані системи',
      'Прозора, орієнтована на результат співпраця'
    ],
    
    // ========== SECTION 8: ZERO-RISK START ==========
    zeroRisk: 'СТАРТ БЕЗ РИЗИКУ',
    zeroRiskDesc: 'Ви не платите наперед. Спочатку ми надаємо працюючий proof of concept — потім ви вирішуєте, як рухатися далі.',
    zeroRiskBullets: [
      'Без передоплат',
      'Без прихованих платежів',
      'Результат перед оплатою'
    ],
    
    // ========== SECTION 9: HOW WE WORK ==========
    howWeWork: 'ЯК МИ ПРАЦЮЄМО',
    howWeWorkSteps: [
      'Product discovery та аналіз',
      'Архітектура та проектування системи',
      'Вибір технологій',
      'Proof of concept',
      'Продакшн-розробка та запуск'
    ],
    // Core Engineering Skills
    coreEngineering: 'ОСНОВНІ ІНЖЕНЕРНІ НАВИЧКИ',
    ourExpertise: 'Наші навички',
    webDevelopment: {
      title: 'Веб-розробка',
      desc: 'Сучасні, адаптивні та високопродуктивні веб-додатки, створені з передовими технологіями:\n\n• Frontend: React, Next.js, Vue.js, Angular, TypeScript\n• Стилізація: Tailwind CSS, CSS3, SASS, Styled Components\n• Управління станом: Redux, MobX, Zustand, Context API\n• Інструменти збірки: Webpack, Vite, Rollup\n• Тестування: Jest, Cypress, Testing Library\n• Продуктивність: Розділення коду, ліниве завантаження, SEO-оптимізація\n\nМи створюємо швидкі, масштабовані та легко підтримувані веб-додатки з виключним досвідом користувача.'
    },
    mobileDevelopment: {
      title: 'Мобільна розробка',
      desc: 'Нативні та кросплатформенні мобільні додатки для iOS та Android:\n\n• Кросплатформенні: React Native, Flutter, Expo\n• iOS Native: Swift, SwiftUI, UIKit, Core Data\n• Android Native: Kotlin, Jetpack Compose, Room\n• Мобільні API: Push-сповіщення, камера, геолокація\n• Управління станом: Redux, MobX, Provider\n• Тестування: Detox, Appium, XCTest, Espresso\n\nСтворюйте один раз, розгортайте скрізь - або обирайте нативну розробку для максимальної продуктивності!'
    },
    backendDatabases: {
      title: 'Бекенд та бази даних',
      desc: 'Надійні серверні рішення та ефективне управління даними:\n\n• Backend: Node.js, Python, Go, Java, .NET\n• Фреймворки: Express, Django, Flask, Spring Boot\n• Бази даних: PostgreSQL, MySQL, MongoDB, Redis\n• Хмарні сервіси: Supabase, Firebase, AWS, Google Cloud\n• API: RESTful, GraphQL, WebSockets, gRPC\n• Аутентифікація: JWT, OAuth, Auth0, Passport\n• DevOps: Docker, Kubernetes, CI/CD конвеєри\n\nМасштабована архітектура для мільйонів користувачів та транзакцій.'
    },
    apis: {
      title: 'API та інтеграції',
      desc: 'Безшовні інтеграції для з\'єднання ваших систем та автоматизації робочих процесів:\n\n• Розробка API: RESTful, GraphQL, WebSockets\n• Сторонні інтеграції: Платіжні шлюзи, соціальні мережі, CRM-системи\n• Популярні сервіси: Stripe, PayPal, Twilio, SendGrid\n• Аутентифікація: OAuth 2.0, JWT, API ключі\n• Документація: OpenAPI/Swagger, Postman\n• Безпека: Обмеження швидкості, шифрування, CORS\n• Webhooks: Обробка подій у реальному часі\n\nПідключайте будь-що до всього - від обробки платежів до AI сервісів.'
    },
    ecommerce: {
      title: 'Електронна комерція',
      desc: 'Повні рішення для електронної комерції від концепції до запуску:\n\n• Платформи: Індивідуальні рішення, Shopify, WooCommerce\n• Інтеграція платежів: Stripe, PayPal, Square, Crypto\n• Управління інвентарем: Відстеження у реальному часі, складування\n• Кошик покупок: Безпечна оплата, обробка замовлень\n• Управління користувачами: Акаунти, списки бажань, історія замовлень\n• Панель адміністратора: Аналітика продажів, управління товарами\n• Маркетинг: SEO, email-кампанії, промокоди\n\nПеретворіть вашу бізнес-ідею на прибутковий онлайн-магазин з продажами 24/7.'
    },
    ai: {
      title: 'AI та LLM рішення',
      desc: 'Передові AI рішення на базі новітніх мовних моделей:\n\n• Інтеграція LLM: GPT-4, Claude, Gemini, Llama\n• AI інструменти: LangChain, Hugging Face, OpenAI API\n• Варіанти використання: Чатботи, генерація контенту, аналіз\n• RAG системи: Власні бази знань, питання-відповіді по документах\n• Векторні БД: Pinecone, Weaviate, Chroma\n• Fine-tuning: Навчання та оптимізація моделей\n• Оптимізація токенів: Зниження витрат, prompt engineering\n• AI агенти: Автономне виконання завдань\n\nВикористовуйте силу AI для автоматизації, аналізу та інновацій як ніколи раніше.'
    },
    webScraping: {
      title: 'Веб-скрапінг',
      desc: 'Професійні рішення для витягу даних та веб-скрапінгу:\n\n• Інструменти: Scrapy, BeautifulSoup, Playwright, Puppeteer\n• Техніки: Динамічний контент, рендеринг JavaScript\n• Анти-детект: Проксі, відбитки браузера, розв\'язання CAPTCHA\n• Обробка даних: Очищення, трансформація, зберігання\n• Автоматизація: Планове витягування, моніторинг\n• Провайдери: Bright Data, ScraperAPI, Oxylabs\n• Відповідність: Дотримання robots.txt, обмеження швидкості\n\nВитягуйте цінні дані з будь-якого сайту для дослідження ринку, моніторингу цін або генерації лідів.'
    },
    aiAgents: {
      title: 'Створення AI агентів',
      desc: 'Інтелектуальні агенти автоматизації, що працюють 24/7 для вашого бізнесу:\n\n• Автоматизація процесів: n8n, Zapier, Make.com\n• AI асистенти: Власні чатботи, віртуальні помічники\n• Автоматизація завдань: Обробка email, введення даних\n• Тестування та QA: Selenium, Playwright, автоматизоване тестування\n• Моніторинг: Перевірка стану систем, сповіщення\n• Інтеграція: З\'єднання різних сервісів та API\n• Бізнес-логіка: Складні робочі процеси прийняття рішень\n\nСтворюйте автономних агентів для рутинних завдань, звільняючи команду для стратегічної роботи.'
    },
    // Core Competencies Section
    coreCompetencies: {
      title: 'Наші компетенції',
      mainTitle: 'Технічна досконалість та інновації',
      subtitle: 'Опановуємо передові технології та кращі галузеві практики для створення інноваційних рішень, що просувають ваш бізнес вперед.',
      programmingLanguages: 'МОВИ ПРОГРАМУВАННЯ',
      softSkills: 'Основні інженерні навички',
      professionalMindset: 'Професійне мислення',
      professionalFoundation: 'НАША ПРОФЕСІЙНА ОСНОВА',
      skillsList: {
        problemSolving: 'Вирішення проблем і комунікація',
        teamwork: 'Командна робота та співпраця',
        adaptability: 'Адаптивність',
        timeManagement: 'Управління часом',
        attentionToDetail: 'Увага до деталей'
      },
      mindsetList: {
        continuousLearning: 'Постійне навчання',
        userCentric: 'Клієнтоорієнтоване мислення',
        securityAwareness: 'Обізнаність з безпекою',
        businessUnderstanding: 'Розуміння бізнесу',
        ownership: 'Відповідальність та власність'
      }
    },
    // Pricing Section
    pricing: {
      title: 'Прозоре ціноутворення',
      subtitle: 'Наші погодинні ставки варіюються від $20 до $70 в залежності від складності проекту, необхідної експертизи, швидкості доставки та тривалості проекту.',
      hourlyRates: 'Погодинні ставки',
      perHour: 'за годину, в залежності від:',
      factors: {
        complexity: 'Складність проекту',
        expertise: 'Необхідна експертиза',
        speed: 'Швидкість доставки',
        duration: 'Тривалість проекту'
      },
      supportMaintenance: 'Підтримка та обслуговування',
      supportPlansCards: {
        basicSupport: 'Базова підтримка',
        standardSupport: 'Стандартна підтримка',
        premiumSupport: 'Преміум підтримка'
      },
      supportPlansPrices: {
        basicSupport: 'від $200/місяць',
        standardSupport: 'від $500/місяць',
        premiumSupport: 'від $800/місяць'
      },
      freeSupport: '1 місяць безкоштовної підтримки включено для всіх проектів, розроблених нашою командою',
      supportPlans: {
        basicSupport: {
          title: 'Базова підтримка',
          desc: 'Основне обслуговування та підтримка:\n\n• Виправлення критичних помилок\n• Оновлення безпеки\n• Підтримка через email\n• Час відповіді: 48 годин\n\nІдеально для стабільних додатків, що потребують мінімальної уваги.'
        },
        standardSupport: {
          title: 'Стандартна підтримка',
          desc: 'Комплексний пакет підтримки:\n\n• Все з Базової\n• Додавання нових функцій\n• Оптимізація продуктивності\n• Пріоритетна підтримка\n• Час відповіді: 24 години\n\nІдеально для зростаючих додатків, що потребують регулярних оновлень.'
        },
        premiumSupport: {
          title: 'Преміум підтримка',
          desc: 'Повна підтримка корпоративного рівня:\n\n• Все зі Стандартної\n• Персональний менеджер\n• Відеоконсультації\n• Щотижневі звіти\n• Час відповіді: 4 години\n\nІдеально для критично важливих додатків, що потребують максимальної уваги.'
        }
      }
    },
    // Fixed Price Projects Section
    fixedPriceProjects: {
      title: 'ПРОЄКТИ З ФІКСОВАНОЮ ЦІНОЮ',
      apiIntegration: {
        title: 'API Інтеграція',
        price: 'від $300',
        desc: 'Безшовні інтеграції API для підключення ваших систем та автоматизації робочих процесів.',
        features: [
          'Інтеграція сторонніх API',
          'Розробка індивідуального ПЗ',
          'Синхронізація даних',
          'Налаштування аутентифікації',
          'Обробка помилок та моніторинг',
          '2 місяці підтримки включено'
        ]
      },
      websiteCreation: {
        title: 'Створення веб-сайту',
        price: 'від $300',
        desc: 'Професійні веб-сайти, створені з використанням сучасних технологій та найкращих практик.',
        features: [
          'Адаптивний дизайн',
          'Сучасний UI/UX',
          'SEO оптимізація',
          'Управління контентом',
          'Оптимізація продуктивності',
          '3 місяці підтримки включено'
        ]
      }
    }
  }
};